import React, { ReactChild, ReactFragment } from 'react';
import classNames from 'classnames';

import { ReactComponent as HelpIcon } from '../../shared/icons/help-icon.svg';
import styles from './styles.module.scss';
import { PlacesType, Tooltip as ReactTooltip } from 'react-tooltip';

// Type mapping for placement compatibility
type Placement =
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
  | 'top-start'
  | 'top-end'
  | 'bottom-start'
  | 'bottom-end'
  | 'left-start'
  | 'left-end'
  | 'right-start'
  | 'right-end';

interface TooltipProps {
  children: ReactChild | ReactFragment;
  placement?: Placement;
  testId: string;
  variant?: 'primary' | 'secondary' | 'confirmation' | 'error';
  icon?: ReactChild | ReactFragment;
  show?: boolean;
}

interface ClickableTooltipProps {
  content: ReactChild | ReactFragment;
  children?: ReactChild | ReactFragment;
  place?: PlacesType | undefined;
}

interface BoxTooltipProps {
  children: ReactChild | ReactFragment;
  id: string;
  isOpen?: boolean;
}

// Utility function to generate unique IDs
const generateTooltipId = (testId: string, variant?: string) => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substr(2, 5);
  return `tooltip-${variant || 'default'}-${testId}-${timestamp}-${random}`;
};

// Map react-bootstrap placement to react-tooltip placement
const mapPlacement = (placement: Placement): PlacesType => {
  const placementMap: Record<Placement, PlacesType> = {
    top: 'top',
    bottom: 'bottom',
    left: 'left',
    right: 'right',
    'top-start': 'top-start',
    'top-end': 'top-end',
    'bottom-start': 'bottom-start',
    'bottom-end': 'bottom-end',
    'left-start': 'left-start',
    'left-end': 'left-end',
    'right-start': 'right-start',
    'right-end': 'right-end',
  };
  return placementMap[placement] || 'top';
};

const ConfirmationTooltip = ({
  children,
  placement = 'top',
  testId,
  show,
  variant = 'confirmation',
  icon = <HelpIcon className={classNames('mx-1', 'text-primary', styles.root)} />,
}: TooltipProps) => {
  const tooltipId = generateTooltipId(testId, 'confirmation');

  return (
    <>
      <span id={tooltipId} tabIndex={0} style={{ fontWeight: 'normal', display: 'inline-block' }}>
        {icon}
      </span>
      <ReactTooltip
        anchorSelect={`#${tooltipId}`}
        place={mapPlacement(placement)}
        data-testid={testId}
        isOpen={show}
        positionStrategy="fixed"
        className={classNames(styles[`confirmationTooltip_${placement}`], {
          [styles.confirmationTooltip]: variant === 'confirmation',
        })}
        style={{
          backgroundColor: '#006c90',
          color: 'white',
          maxWidth: '380px',
          minWidth: '150px',
          width: 'auto',
          zIndex: 1000000,
          fontSize: '14px',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
          fontWeight: 'normal', // Reset font-weight to prevent inheritance
          fontStyle: 'normal', // Reset font-style
          textDecoration: 'none', // Reset text-decoration
          textTransform: 'none', // Reset text-transform
          letterSpacing: 'normal', // Reset letter-spacing
          padding: '12px 14px',
          borderRadius: '4px',
          whiteSpace: 'normal',
          wordWrap: 'break-word',
          wordBreak: 'break-word',
          lineHeight: '1.5',
          textAlign: 'left',
          display: 'block',
        }}
        opacity={1}
      >
        {children}
      </ReactTooltip>
    </>
  );
};

const ErrorTooltip = ({
  children,
  placement = 'top',
  testId,
  show,
  variant = 'error',
  icon = <HelpIcon className={classNames('mx-1', 'text-primary', styles.root)} />,
}: TooltipProps) => {
  const tooltipId = generateTooltipId(testId, 'error');

  return (
    <>
      <span id={tooltipId} tabIndex={0} style={{ fontWeight: 'normal', display: 'inline-block' }}>
        {icon}
      </span>
      <ReactTooltip
        anchorSelect={`#${tooltipId}`}
        place={mapPlacement(placement)}
        data-testid={testId}
        isOpen={show}
        positionStrategy="fixed"
        className={classNames(styles[`errorTooltip_${placement}`], {
          [styles.errorTooltip]: variant === 'error',
        })}
        style={{
          backgroundColor: '#f14949',
          color: 'white',
          maxWidth: '380px',
          minWidth: '150px',
          width: 'auto',
          zIndex: 1000000,
          fontSize: '14px',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
          fontWeight: 'normal', // Reset font-weight to prevent inheritance
          fontStyle: 'normal', // Reset font-style
          textDecoration: 'none', // Reset text-decoration
          textTransform: 'none', // Reset text-transform
          letterSpacing: 'normal', // Reset letter-spacing
          padding: '12px 14px',
          borderRadius: '4px',
          whiteSpace: 'normal',
          wordWrap: 'break-word',
          wordBreak: 'break-word',
          lineHeight: '1.5',
          textAlign: 'left',
          display: 'block',
        }}
        opacity={1}
      >
        {children}
      </ReactTooltip>
    </>
  );
};

const ClickableTooltip = ({
  content,
  children = <HelpIcon className={classNames('mx-1', 'text-primary', styles.root)} />,
  place = 'right',
}: ClickableTooltipProps) => {
  return (
    <>
      <span id="clickable" style={{ fontWeight: 'normal', display: 'inline-block' }}>
        {children}
      </span>
      <ReactTooltip
        positionStrategy="fixed"
        className={styles.clickableTooltip}
        style={{
          zIndex: 1000000,
          color: '#18191b',
          backgroundColor: '#ddebef',
          fontWeight: 'normal', // Explicitly set to normal
          fontStyle: 'normal', // Reset font-style
          textDecoration: 'none', // Reset text-decoration
          textTransform: 'none', // Reset text-transform
          letterSpacing: 'normal', // Reset letter-spacing
          maxWidth: '380px',
          minWidth: '150px',
          width: 'auto',
          textAlign: 'left',
          padding: '12px 14px',
          fontSize: '14px',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
          borderRadius: '4px',
          whiteSpace: 'normal',
          wordWrap: 'break-word',
          wordBreak: 'break-word',
          lineHeight: '1.5',
          display: 'block',
        }}
        opacity={0}
        anchorSelect="#clickable"
        place={place}
        clickable
      >
        {content}
      </ReactTooltip>
    </>
  );
};

const BoxTooltip = ({ children, id, isOpen }: BoxTooltipProps) => {
  return (
    <ReactTooltip
      anchorSelect={`#${id}`}
      positionStrategy="fixed"
      style={{
        zIndex: '9999',
        color: '#18191b',
        backgroundColor: '#fff',
        fontWeight: 'normal',
        maxWidth: '250px',
        borderRadius: '12px',
        padding: '0px',
        boxShadow: '0 2px 5px 0 rgba(0, 0, 0, 0.15)',
      }}
      className={styles.boxTooltip}
      classNameArrow="tooltip-arrow"
      place="bottom"
      isOpen={isOpen}
      clickable
    >
      {children}
    </ReactTooltip>
  );
};

const Tooltip = ({
  children,
  placement = 'top',
  testId,
  show,
  variant = 'primary',
  icon = <HelpIcon className={classNames('mx-1', 'text-primary', styles.root)} />,
}: TooltipProps) => {
  const tooltipId = generateTooltipId(testId, 'primary');

  // Determine if content needs wrapping based on its type - only for actual table tooltips
  // Exclude instruction tooltips that should wrap normally
  const isTableContent =
    (testId?.includes('approvers') || testId?.includes('signers')) &&
    !testId?.includes('insert-signers') &&
    !testId?.includes('add-signers') &&
    !testId?.includes('send-all-at-once');

  return (
    <>
      <span id={tooltipId} tabIndex={0} style={{ fontWeight: 'normal', display: 'inline-block' }}>
        {icon}
      </span>
      <ReactTooltip
        anchorSelect={`#${tooltipId}`}
        place={mapPlacement(placement)}
        data-testid={testId}
        isOpen={show}
        positionStrategy="fixed"
        className={classNames(styles[`primaryTooltip_${placement}`], {
          [styles.primaryTooltip]: variant === 'primary',
        })}
        style={{
          backgroundColor: 'rgba(221, 235, 239, 1)', // Ensure fully opaque background
          color: '#18191b',
          maxWidth: '380px', // Increased for better content accommodation
          minWidth: isTableContent ? '240px' : '150px', // Dynamic min width
          width: 'auto', // Auto width to fit content
          zIndex: 1000000, // Very high z-index to ensure it appears above all dropdowns
          fontSize: '14px',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
          fontWeight: 'normal', // Reset font-weight to prevent inheritance
          fontStyle: 'normal', // Reset font-style
          textDecoration: 'none', // Reset text-decoration
          textTransform: 'none', // Reset text-transform
          letterSpacing: 'normal', // Reset letter-spacing
          padding: '12px 14px', // Consistent padding
          borderRadius: '4px',
          whiteSpace: isTableContent ? 'nowrap' : 'normal', // Nowrap for tables, normal for text
          wordWrap: 'break-word', // Allow word wrapping for long text
          wordBreak: 'break-word', // Break long words if needed
          lineHeight: '1.5',
          textAlign: 'left',
          display: 'block', // Ensure block display for proper wrapping
        }}
        opacity={1}
      >
        {children}
      </ReactTooltip>
    </>
  );
};

export { ClickableTooltip, ConfirmationTooltip, BoxTooltip, ErrorTooltip };

export default Tooltip;
